import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  Download,
  ExternalLink,
  Loader2,
  Search,
  Smartphone,
  User,
  Wrench,
} from 'lucide-react';
import PosV2SavTimeline from './PosV2SavTimeline';
import PosV2SavRepairsTable from './PosV2SavRepairsTable';
import RepairResteDisplay, { availableAvoirs } from '../reparations/RepairResteDisplay';
import { fmt } from './posV2Utils';
import { printTicket80mm, downloadAvoirTicketPdf } from '../../utils/ticket80mmPrint';
import {
  buildSavCartLine,
  formatPhoneDisplay,
  repairFinancials,
  savStatusTone,
} from './posV2SavUtils';

const PosV2SavDrawer = ({ api, sessionMag, onAddLine, onAttachClient, onClose }) => {
  const [query, setQuery] = useState('');
  const [repairs, setRepairs] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [montant, setMontant] = useState('');
  const [showAcompteForm, setShowAcompteForm] = useState(false);
  const [searching, setSearching] = useState(false);
  const [avoirs, setAvoirs] = useState([]);

  const loadRepairs = useCallback(async (searchQ) => {
    setLoadingList(true);
    try {
      const params = { limit: 30, include_delivered: false, magasin_id: sessionMag };
      if (searchQ?.trim().length >= 2) params.search = searchQ.trim();
      const res = await api.get('/devices', { params });
      const items = res.data?.devices || res.data || [];
      setRepairs(items.filter((d) => !['Livré', 'Refusé', 'Non Réparable'].includes(d.status)));
    } catch {
      setRepairs([]);
    } finally {
      setLoadingList(false);
    }
  }, [api, sessionMag]);

  useEffect(() => { loadRepairs(''); }, [loadRepairs]);

  const loadDetail = async (device) => {
    if (!device?.ref) return;
    setLoadingDetail(true);
    setAvoirs([]);
    try {
      const [devRes, avoirRes] = await Promise.all([
        api.get(`/devices/${device.ref}`),
        api.get('/avoirs', { params: { device_ref: device.ref, limit: 20 } }).catch(() => ({ data: [] })),
      ]);
      setSelected(devRes.data);
      setAvoirs(Array.isArray(avoirRes.data) ? avoirRes.data : []);
      const { reste } = repairFinancials(devRes.data);
      setMontant(reste > 0 ? String(reste) : '');
    } catch {
      setSelected(device);
      setAvoirs([]);
      const { reste } = repairFinancials(device);
      setMontant(reste > 0 ? String(reste) : '');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSearch = async () => {
    setSearching(true);
    await loadRepairs(query);
    setSearching(false);
  };

  const handleSelect = (device) => {
    setShowAcompteForm(false);
    loadDetail(device);
  };

  const pushLine = (line) => {
    if (!line || !selected) return;
    // Rattacher le client de la réparation au ticket POS → facture visible espace client
    const nom = (selected.client_name || '').trim();
    const tel = (selected.client_phone || selected.client_tel || '').trim();
    if (onAttachClient && (nom || tel)) {
      onAttachClient({ nom: nom || 'Client', telephone: tel });
    }
    onAddLine(line);
    onClose();
  };

  const encaisserReste = () => {
    if (!selected) return;
    const { reste } = repairFinancials(selected);
    if (reste <= 0) return;
    pushLine(buildSavCartLine(selected, reste, 'reste'));
  };

  const addAcompte = () => {
    if (!selected) return;
    const amount = parseFloat(montant);
    if (!amount || amount <= 0) return;
    pushLine(buildSavCartLine(selected, amount, 'acompte'));
  };

  const { acompte, reste, total } = repairFinancials(selected);
  const avoirsDispo = availableAvoirs(avoirs);
  const statusTone = selected ? savStatusTone(selected.status) : null;
  const photoUrl = selected?.photos?.[0]
    ? `${process.env.REACT_APP_BACKEND_URL}${selected.photos[0]}`
    : null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
          <input
            className="w-full pl-9 pr-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white min-h-[52px]"
            placeholder="REM, client, téléphone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={searching}
          className="px-5 py-3 rounded-lg bg-red-600 font-semibold min-h-[52px] flex items-center gap-2"
        >
          {searching ? <Loader2 size={16} className="animate-spin" /> : null}
          Chercher
        </button>
      </div>

      {selected ? (
        <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-4 text-gray-200">
          {loadingDetail && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 size={16} className="animate-spin" /> Chargement dossier…
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[240px] space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Wrench className="text-red-500" size={20} />
                <span className="text-xl font-bold">{selected.ref}</span>
                {statusTone && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold border uppercase ${statusTone.className}`}>
                    {statusTone.label}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <User size={14} className="text-gray-500 shrink-0" />
                <span className="font-medium">{selected.client_name || '—'}</span>
                <span className="text-gray-500">{formatPhoneDisplay(selected.client_phone)}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                  {photoUrl ? (
                    <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Smartphone className="text-gray-500" size={24} />
                  )}
                </div>
                <div>
                  <div className="font-semibold">{selected.model || 'Appareil'}</div>
                  {selected.problem && (
                    <div className="text-xs text-gray-500 truncate max-w-[280px]">{selected.problem}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 items-start">
              <div className="rounded-lg bg-black/30 border border-white/10 text-white px-4 py-3 min-w-[130px]">
                <div className="text-[10px] uppercase text-gray-500 font-semibold">Acompte encaissé</div>
                <div className="text-lg font-bold text-emerald-400 mt-1">{fmt(acompte)} €</div>
              </div>
              <div className={`rounded-lg bg-black/30 px-4 py-3 min-w-[130px] ${avoirsDispo.length ? 'border border-emerald-500/30' : 'border border-red-900/40'}`}>
                <div className="text-[10px] uppercase text-gray-500 font-semibold">Reste à payer</div>
                <div className={`text-2xl font-bold mt-1 ${avoirsDispo.length || reste <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  <RepairResteDisplay
                    reste={reste}
                    avoirs={avoirs}
                    onPrintAvoir={(refAvoir) => printTicket80mm(api, { type: 'avoir', ref: refAvoir }).catch((err) => alert(err.response?.data?.detail || err.message))}
                    onDownloadAvoirPdf={(refAvoir) => downloadAvoirTicketPdf(api, refAvoir).catch((err) => alert(err.response?.data?.detail || err.message))}
                  />
                </div>
                {avoirsDispo[0]?.ref && (
                  <div className="text-[10px] text-gray-500 mt-0.5 font-mono">{avoirsDispo[0].ref}</div>
                )}
                {total > 0 && (
                  <div className="text-[10px] text-gray-500 mt-0.5">Total réparation {fmt(total)} €</div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 w-full sm:w-auto sm:min-w-[200px]">
              <button
                type="button"
                onClick={encaisserReste}
                disabled={reste <= 0}
                className="min-h-[56px] px-4 py-3 rounded-xl bg-red-600 font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CreditCard size={18} />
                Encaisser reste ({fmt(reste)} €)
              </button>
              <button
                type="button"
                onClick={() => setShowAcompteForm((v) => !v)}
                className="min-h-[44px] px-4 py-2 rounded-lg bg-white/10 font-semibold flex items-center justify-center gap-2 hover:bg-black/55 text-gray-100"
              >
                <Download size={16} />
                Acompte
              </button>
              <Link
                to={`/reparations/${selected.ref}`}
                className="min-h-[44px] px-4 py-2 rounded-lg bg-black/30 border-white/15 font-semibold flex items-center justify-center gap-2 hover:bg-black/45 text-gray-200 text-center"
                onClick={onClose}
              >
                <ExternalLink size={16} />
                Voir fiche
              </Link>
            </div>
          </div>

          {showAcompteForm && (
            <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-950/20 space-y-3">
              <label className="block text-xs text-gray-400">
                Montant acompte (€)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white min-h-[52px]"
                  value={montant}
                  onChange={(e) => setMontant(e.target.value)}
                />
              </label>
              <button
                type="button"
                onClick={addAcompte}
                className="w-full min-h-[52px] py-3 rounded-lg bg-amber-600 font-bold"
              >
                Ajouter acompte au panier
              </button>
            </div>
          )}

          <PosV2SavTimeline device={selected} />
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-gray-400 text-sm">
          Recherchez ou sélectionnez un dossier REM pour afficher la fiche SAV.
        </div>
      )}

      {loadingList ? (
        <div className="flex items-center justify-center gap-2 py-8 text-gray-400">
          <Loader2 size={18} className="animate-spin" /> Chargement réparations…
        </div>
      ) : (
        <PosV2SavRepairsTable
          repairs={repairs}
          selectedRef={selected?.ref}
          onSelect={handleSelect}
        />
      )}
    </div>
  );
};

export default PosV2SavDrawer;
